import React from 'react';
import { Box } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Column } from 'react-table';

interface E55ColumnsOptions {
  userData?: { designatedOperator?: string };
  tableType?: 'all' | 'inbound' | 'outbound';
}

// Create base columns for e55 tables
export const getE55BaseColumns = (userData?: { designatedOperator?: string }): Column<any>[] => [
  {
    Header: 'Todo',
    accessor: 'pendingOn',
    width: '70px',
    Cell: ({ value }: { value: any }) => {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {value === userData?.designatedOperator ? (
            <StarIcon
              sx={{
                color: '#FFC107', // Yellow color for the star
                fontSize: '16px',
              }}
            />
          ) : (
            <StarBorderIcon
              sx={{
                color: '#666666', // Grey color for the empty star
                fontSize: '16px',
              }}
            />
          )}
        </Box>
      );
    },
  },
  {
    Header: 'ID',
    accessor: 'fileId',
    width: '70px',
  },
  {
    Header: 'Status',
    accessor: 'fileStatus',
    width: '80px',
    Cell: ({ value }: { value: any }) => value || '-',
  },
  // PPO column will be dynamically added based on table type
  {
    Header: 'Year',
    accessor: 'accountingYear',
    width: '70px',
    Cell: ({ value }: { value: any }) => value || '-',
  },
  {
    Header: 'Quarter',
    accessor: 'accountingQuarter',
    width: '70px',
    Cell: ({ value }: { value: any }) => value || '-',
  },
  {
    Header: 'Version',
    accessor: 'fileVersion',
    width: '80px',
    Cell: ({ value }: { value: any }) => value,
  },
  {
    Header: 'Uploaded By',
    accessor: 'operatorByFileUploader',
    width: '100px',
    Cell: ({ value }: { value: any }) => value || '-',
  },
  {
    Header: 'Downloaded',
    accessor: 'downloadDate',
    width: '100px',
    Cell: ({ value }: { value: any }) => value || '-',
  },
  {
    Header: 'Inb. Acc.',
    accessor: 'inboundAcceptanceDate',
    width: '100px',
    Cell: ({ value }: { value: any }) => value || '-',
  },
  {
    Header: 'Outb. Acc.',
    accessor: 'outboundAcceptanceDate',
    width: '100px',
    Cell: ({ value }: { value: any }) => value || '-',
  },
  {
    Header: '# Despatches',
    accessor: 'samples',
    width: '120px',
    Cell: ({ value }: { value: any }) => value || '0',
  },
  {
    Header: 'Filename',
    accessor: 'fileName',
    width: '200px',
  },
  {
    Header: 'Comment',
    accessor: 'comment',
    width: '150px',
    Cell: ({ value }: { value: any }) => {
      if (value === true) return 'Y';
      if (value === false) return '';
      return value !== null && value !== undefined ? String(value) : '';
    },
  },
  {
    Header: 'Removal DO',
    accessor: 'removalDo',
    width: '100px',
    Cell: ({ value }: { value: any }) => value || '-',
  },
  {
    Header: 'Removal Date',
    accessor: 'removalDate',
    width: '100px',
    Cell: ({ value }: { value: any }) => value || '-',
  },
];

// PPO column definitions
export const ppoColumnOutbound = {
  Header: 'DO',
  accessor: 'operatorByInboundPpo',
  width: '100px',
  Cell: ({ value }: { value: any }) => value || '-',
};

export const ppoColumnDefault = {
  Header: 'DO',
  accessor: 'operatorByOutboundPpo',
  width: '100px',
  Cell: ({ value }: { value: any }) => value || '-',
};

// DO column definition with row-based logic for 'all' table type
const getDOColumnRowBased = (userData?: { designatedOperator?: string }) => ({
  Header: 'DO',
  id: 'do',
  accessor: 'operatorByInboundPpo',
  width: '100px',
  Cell: (props: any) => {
    const row = props.row;
    // Check if operatorByInboundPpo equals current user's designatedOperator
    if (row?.operatorByInboundPpo === userData?.designatedOperator) {
      return row?.operatorByOutboundPpo;
    }
    // Otherwise, show operatorByInboundPpo (because it's outbound)
    return row?.operatorByInboundPpo || '-';
  },
});

// Generate complete column definitions based on table type
export const getE55Columns = (options: E55ColumnsOptions = {}): Column<any>[] => {
  const { userData, tableType = 'all' } = options;
  const columns = [...getE55BaseColumns(userData)];
  
  // Use row-based DO column for 'all' table type, otherwise use standard columns
  const ppoColumn = tableType === 'all' 
    ? getDOColumnRowBased(userData)
    : tableType === 'outbound' 
      ? ppoColumnOutbound 
      : ppoColumnDefault;

  // Insert PPO column after Status (at index 2)
  columns.splice(2, 0, ppoColumn);

  return columns;
}; 